<script lang="ts" setup>
const { t } = useI18n()

// 定义 props
interface Props {
  show: boolean
}

const props = defineProps<Props>()

// 定义 emits
const emit = defineEmits<{
  'update:show': [value: boolean]
}>()

// 追踪 mousedown 是否发生在遮罩层上
const mouseDownOnOverlay = ref(false)

// 遮罩层 mousedown 事件处理
const handleOverlayMouseDown = (e: MouseEvent) => {
  mouseDownOnOverlay.value = e.button === 0 && e.target === e.currentTarget
}

// 遮罩层点击事件处理
const handleOverlayClick = (e: MouseEvent) => {
  if (e.button === 0 && mouseDownOnOverlay.value) {
    closeDialog()
  }
  mouseDownOnOverlay.value = false
}

// 关闭弹窗
const closeDialog = () => {
  emit('update:show', false)
}
</script>

<template>
  <div
    v-if="props.show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
    @mousedown="handleOverlayMouseDown"
    @click.self="handleOverlayClick"
  >
    <!-- web端 -->
    <div
      class="hidden md:block relative bg-[#FFF] rounded-[16px] w-[580px] p-[32px] pb-[52px]"
    >
      <!-- 关闭按钮 -->
      <button
        class="absolute top-[32px] right-[32px] w-[20px] h-[20px] flex items-center justify-center text-[#999] hover:text-[#666] transition-colors"
        @click="closeDialog"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <!-- 标题 -->
      <h2 class="text-[20px] font-medium text-[#000] mb-[54px]">
        {{ t('businessDialog.title') }}
      </h2>

      <!-- 二维码区域 -->
      <div class="flex flex-col items-center mb-[32px]">
        <!-- 二维码占位 -->
        <div class="w-[220px] h-[220px] mb-[20px]">
          <img
            src="https://images-cloud.qboson.com/website/qrod.png"
            :alt="t('businessDialog.qrcodeAlt')"
            class="w-full h-full object-contain"
          />
        </div>
        <p class="text-[20px] text-[#000] font-medium">
          {{ t('businessDialog.qrcodeLabel') }}
        </p>
      </div>

      <!-- 联系方式 -->
      <div class="space-y-[20px]">
        <!-- 电话 -->
        <div
          class="flex items-center h-[47px] px-[20px] bg-[#F6F7F9] rounded-[2px]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="30"
            viewBox="0 0 30 30"
            fill="none"
          >
            <path
              d="M21.5971 27.0465C20.4186 27.0465 19.0716 26.8322 17.6635 26.3884C14.2961 25.3322 10.9747 23.1435 8.32672 20.22C4.34713 15.8271 2.43387 11.4496 2.81652 7.54653C2.92366 6.58224 3.24509 5.35775 4.69917 4.36285C5.81652 3.4751 7.08693 2.93938 8.08183 2.97C9.12264 2.9853 9.48999 3.35265 10.5614 4.92918C12.1992 7.19449 12.3216 8.32714 12.2604 8.97C12.1533 10.0414 11.5563 10.5006 11.0359 10.9139C10.8982 11.021 10.7757 11.1129 10.6686 11.22C10.0257 12.0465 9.22978 13.1792 12.6124 16.669C15.5053 19.4394 17.0512 19.7761 17.7094 19.7302C18.2145 19.6996 18.4288 19.4241 18.5053 19.3322L18.5206 19.3169C19.592 17.8016 19.9594 17.4343 20.9849 17.2812C21.9951 17.1282 22.5002 17.4343 24.6277 18.7812C26.5563 19.9904 27.4441 20.8016 27.2298 22.0567C27.1686 23.4496 26.1124 25.3322 24.9033 26.1741C24.0767 26.7557 22.9441 27.0465 21.5971 27.0465ZM8.0053 4.17918C7.45428 4.17918 6.47468 4.47 5.41856 5.32714L5.38795 5.35775C4.40836 6.01591 4.13285 6.76591 4.0257 7.66898C3.68897 11.2047 5.47979 15.2608 9.22979 19.3935C13.3012 23.8782 18.3522 25.822 21.6277 25.822C22.7145 25.822 23.6022 25.6078 24.1992 25.1945C25.1788 24.5057 25.9747 22.8986 26.0053 21.9955L26.0206 21.9037C26.0818 21.5516 26.143 21.1996 23.9696 19.8373C21.8879 18.521 21.6737 18.4292 21.1686 18.5057C20.6482 18.5822 20.541 18.5975 19.5155 20.0516L19.4849 20.0822C18.9645 20.7404 18.291 20.9241 17.8165 20.97C16.2706 21.0924 14.2349 19.9445 11.7706 17.572L11.7553 17.5567C7.88285 13.5771 8.69407 11.8016 9.73489 10.4547L9.81142 10.3782C9.97979 10.2251 10.1328 10.0873 10.3012 9.96489C10.7757 9.58224 11.0053 9.38326 11.0665 8.86285C11.1277 8.15877 10.6226 7.07204 9.58183 5.63326L9.56652 5.61795C8.60223 4.19449 8.5104 4.19449 8.06652 4.19449C8.03591 4.17918 8.0206 4.17918 8.0053 4.17918Z"
              fill="#333333"
            />
          </svg>
          <span class="ml-[16px] text-[18px] text-[#000] font-normal">{{
            t('businessDialog.phone')
          }}</span>
        </div>

        <!-- 邮箱 -->
        <div
          class="flex items-center h-[47px] px-[20px] bg-[#F6F7F9] rounded-[2px] mb-[52px]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="30"
            viewBox="0 0 30 30"
            fill="none"
          >
            <path
              d="M24.3298 9.30091C24.5984 9.10021 24.9797 9.16663 25.1587 9.41353C25.36 9.68215 25.293 10.0634 25.0244 10.2424L15.5256 17.1419C15.3018 17.2986 15.0332 17.2763 14.831 17.1419L5.31056 10.2424C5.04194 10.0634 4.99718 9.68221 5.17628 9.41359C5.37848 9.16669 5.73656 9.10027 6.00512 9.30097L15.1675 15.9319L24.3298 9.30091ZM5.01962 5.17969H25.3153C25.9875 5.17969 26.5926 5.44831 27.018 5.89591C27.444 6.32191 27.7125 6.92701 27.7125 7.57681V22.6979C27.7125 23.347 27.4439 23.952 27.018 24.4004C26.5702 24.8257 25.9875 25.0942 25.3153 25.0942H5.01962C4.3697 25.0942 3.76544 24.8257 3.3395 24.4004C2.89184 23.952 2.62256 23.3694 2.62256 22.6979V7.57675C2.62256 6.92695 2.89184 6.32185 3.3395 5.89585C3.76544 5.44825 4.36976 5.17969 5.01962 5.17969ZM25.3153 6.36667H5.01962C4.68386 6.36667 4.39214 6.50095 4.16834 6.72475C3.94454 6.94855 3.8102 7.24033 3.8102 7.57675V22.6979C3.8102 23.0336 3.94454 23.3469 4.16834 23.5491C4.39214 23.773 4.68386 23.9072 5.01962 23.9072H25.3153C25.6518 23.9072 25.9652 23.773 26.1891 23.5491C26.3904 23.347 26.5255 23.0336 26.5255 22.6979V7.57675C26.5255 7.24033 26.3904 6.94855 26.1891 6.72475C25.9652 6.50095 25.6518 6.36667 25.3153 6.36667ZM14.831 17.1419C14.7646 17.0972 14.7199 17.0524 14.6967 17.0077Z"
              fill="black"
            />
          </svg>
          <span class="ml-[16px] text-[18px] text-[#000] font-normal">{{
            t('businessDialog.emailUser') +
            '@' +
            t('businessDialog.emailDomain')
          }}</span>
        </div>
      </div>
    </div>

    <!-- 移动端 -->
    <div
      class="md:hidden relative bg-[#FFF] rounded-[16px] w-[311px] p-[14px] pb-[30px]"
    >
      <!-- 关闭按钮 -->
      <button
        class="absolute top-[14px] right-[14px] w-[20px] h-[20px] flex items-center justify-center text-[#999] hover:text-[#666] transition-colors"
        @click="closeDialog"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <!-- 标题 -->
      <h2 class="text-[14px] font-medium text-[#000] mb-[28px]">
        {{ t('businessDialog.title') }}
      </h2>

      <!-- 二维码区域 -->
      <div class="flex flex-col items-center mb-[30px]">
        <!-- 二维码占位 -->
        <div class="w-[120px] h-[120px] mb-[12px]">
          <img
            src="https://images-cloud.qboson.com/website/qrod.png"
            :alt="t('businessDialog.qrcodeAlt')"
            class="w-full h-full object-contain"
          />
        </div>
        <p class="text-[14px] text-[#000] font-normal">
          {{ t('businessDialog.qrcodeLabel') }}
        </p>
      </div>

      <!-- 联系方式 -->
      <div class="space-y-[7px]">
        <!-- 电话 -->
        <div
          class="flex items-center h-[34px] px-[8px] bg-[#F6F7F9] rounded-[2px] mx-[30px]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              d="M12.9582 16.2279C12.251 16.2279 11.4429 16.0993 10.598 15.833C8.57755 15.1993 6.58469 13.8861 4.99591 12.132C2.60816 9.49628 1.4602 6.86975 1.68979 4.52792C1.75408 3.94934 1.94693 3.21465 2.81938 2.61771C3.48979 2.08506 4.25203 1.76363 4.84897 1.782C5.47346 1.79118 5.69387 2.01159 6.33673 2.95751C7.31938 4.31669 7.39285 4.99628 7.35612 5.382C7.29183 6.02485 6.93367 6.30037 6.62142 6.54832C6.53877 6.61261 6.4653 6.66771 6.40101 6.732C6.0153 7.22792 5.53775 7.90751 7.56734 10.0014C9.30305 11.6636 10.2306 11.8657 10.6255 11.8381C10.9286 11.8198 11.0571 11.6544 11.1031 11.5993L11.1122 11.5902C11.7551 10.681 11.9755 10.4606 12.5908 10.3687C13.1969 10.2769 13.5 10.4606 14.7765 11.2687C15.9337 11.9942 16.4663 12.481 16.3377 13.234C16.301 14.0698 15.6673 15.1993 14.9418 15.7044C14.4459 16.0534 13.7663 16.2279 12.9582 16.2279ZM4.80306 2.50751C4.47244 2.50751 3.88469 2.682 3.25101 3.19628L3.23265 3.21465C2.64489 3.60955 2.47959 4.05955 2.4153 4.60139C2.21326 6.72281 3.28775 9.15649 5.53775 11.6361C7.98061 14.3269 11.0112 15.4932 12.9765 15.4932C13.6286 15.4932 14.1612 15.3647 14.5194 15.1167C15.1071 14.7034 15.5847 13.7391 15.6031 13.1973L15.6122 13.1422C15.649 12.931 15.6857 12.7198 14.3816 11.9024C13.1326 11.1126 13.0041 11.0575 12.701 11.1034C12.3888 11.1493 12.3245 11.1585 11.7092 12.031L11.6908 12.0493C11.3786 12.4442 10.9745 12.5544 10.6898 12.582C9.76224 12.6555 8.54081 11.9667 7.06224 10.5432L7.05305 10.534C4.72959 8.14628 5.21632 7.08098 5.84081 6.27281L5.88673 6.2269C5.98775 6.13506 6.07959 6.05241 6.18061 5.97894C6.4653 5.74934 6.60306 5.62996 6.63979 5.31771C6.67652 4.89526 6.37346 4.24322 5.74897 3.37996L5.73979 3.37077C5.16122 2.51669 5.10612 2.51669 4.83979 2.51669C4.82142 2.50751 4.81224 2.50751 4.80306 2.50751Z"
              fill="#333333"
            />
          </svg>
          <span class="ml-[12px] text-[14px] text-[#000] font-normal">{{
            t('businessDialog.phone')
          }}</span>
        </div>

        <!-- 邮箱 -->
        <div
          class="flex items-center h-[34px] px-[8px] bg-[#F6F7F9] rounded-[2px] mx-[30px]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              d="M14.5978 5.5782C14.759 5.45778 14.9878 5.49763 15.0952 5.64577C15.216 5.80694 15.1758 6.03569 15.0146 6.14308L9.31532 10.2828C9.18104 10.3768 9.01987 10.3634 8.89855 10.2828L3.18629 6.14311C3.02511 6.03569 2.99826 5.80698 3.10572 5.64581C3.22704 5.49767 3.44189 5.45782 3.60302 5.57824L9.10048 9.55678L14.5978 5.5782ZM3.01172 3.10547H15.1892C15.5925 3.10547 15.9555 3.26664 16.2107 3.5352C16.4663 3.7908 16.6274 4.15386 16.6274 4.54374V13.6164C16.6274 14.0058 16.4663 14.3689 16.2107 14.6379C15.9421 14.8931 15.5925 15.0542 15.1892 15.0542H3.01172C2.62177 15.0542 2.25921 14.8931 2.00365 14.6379C1.73505 14.3689 1.57349 14.0193 1.57349 13.6164V4.5437C1.57349 4.15382 1.73505 3.79076 2.00365 3.53516C2.25921 3.2666 2.62181 3.10547 3.01172 3.10547ZM15.1892 3.81766H3.01172C2.81027 3.81766 2.63523 3.89822 2.50095 4.0325C2.36667 4.16678 2.28607 4.34185 2.28607 4.5437V13.6164C2.28607 13.8178 2.36667 14.0058 2.50095 14.1271C2.63523 14.2614 2.81027 14.342 3.01172 14.342H15.1892C15.391 14.342 15.5791 14.2615 15.7134 14.1271C15.8342 14.0058 15.9152 13.8178 15.9152 13.6164V4.5437C15.9152 4.34185 15.8342 4.16678 15.7134 4.0325C15.5791 3.89822 15.391 3.81766 15.1892 3.81766ZM8.89855 10.2828C8.85874 10.256 8.83188 10.2291 8.81798 10.2023L8.89855 10.2828Z"
              fill="black"
            />
          </svg>
          <span class="ml-[12px] text-[14px] text-[#000] font-normal">{{
            t('businessDialog.emailUser') +
            '@' +
            t('businessDialog.emailDomain')
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
